import React, { useCallback } from 'react';

import {
  StripePaymentProvider,
  useStripePayment,
} from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { useBraze } from '../../../../context/Braze';
import { type useOrderState } from '../../hooks';
import { BagAnimatedDialog } from './BagAnimatedDialog';

export const BagAnimatedDialogWithStripe = (props: WithStripeProviderProps) => {
  const { orderState, isOrderPlaybackEnabled, orderPlaybackConfig } = props;

  const { total, stripeSession, purchaseAttempt } = orderState;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <StripePaymentProvider
      purchaseAttempt={purchaseAttempt}
      publicKey={stripePublicKey}
      amount={total}
      sessionId={stripeSession}
    >
      <BagAnimatedDialogStripeWrapper
        orderState={orderState}
        isOrderPlaybackEnabled={isOrderPlaybackEnabled}
        orderPlaybackConfig={orderPlaybackConfig}
      />
    </StripePaymentProvider>
  );
};

const BagAnimatedDialogStripeWrapper = (props: WithStripeProviderProps) => {
  const { orderState, isOrderPlaybackEnabled, orderPlaybackConfig } = props;

  const {
    total,
    stripeCustomerId,
    stripeCustomerKey,
    paymentMethods,
    requestOrderPlayback,
    requestOrderSubmission,
  } = orderState;

  // ──────────────────────────────────────────────────────────────────────────

  const { logCustomEvent } = useBraze();
  const requestOrderSubmissionOrPlayback = useCallback(
    (paymentMethodId?: string) => {
      if (isOrderPlaybackEnabled) {
        logCustomEvent('bag.place-order.selected');
        requestOrderPlayback(paymentMethodId);

        return;
      }

      requestOrderSubmission(paymentMethodId);
    },
    [
      isOrderPlaybackEnabled,
      logCustomEvent,
      requestOrderPlayback,
      requestOrderSubmission,
    ],
  );

  // ──────────────────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  const stripePaymentConfig = useStripePayment({
    total,
    stripeCustomerId,
    stripeCustomerKey,
    stripePublicKey,
    paymentMethods,
    trackStripeUsage,
    onConfirm: requestOrderSubmissionOrPlayback,
  });

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <BagAnimatedDialog
      orderState={orderState}
      stripePaymentConfig={stripePaymentConfig}
      isOrderPlaybackEnabled={isOrderPlaybackEnabled}
      orderPlaybackConfig={orderPlaybackConfig}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY as string;

// ─── Types ──────────────────────────────────────────────────────────────────

type WithStripeProviderProps = {
  orderState: ReturnType<typeof useOrderState>;
  isOrderPlaybackEnabled: boolean;
  orderPlaybackConfig: {
    locationFadeIn: number;
    timeFadeIn: number;
    orderDetailsFadeIn: number;
    lineItemsFadeIn: number;
    confirmationFadeIn: number;
  };
};
